/**
 * Application preferences toggles (theme/feature flags as implemented) and related account settings.
 *
 * Where: `/settings` inside dashboard.
 * Uses: local component state and any persistence hooks defined in file.
 * Behavior: saves on explicit actions; read-only sections for unsupported prefs.
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Button,
  TextField,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

export const Settings: React.FC = () => {
  // Dummy settings state
  const [notifications, setNotifications] = React.useState(true);
  const [darkMode, setDarkMode] = React.useState(false);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure your application and account preferences.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
        {/* Application Settings */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Application Settings
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={notifications}
                  onChange={() => setNotifications((v) => !v)}
                  color="primary"
                />
              }
              label="Enable Notifications"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={darkMode}
                  onChange={() => setDarkMode((v) => !v)}
                  color="primary"
                />
              }
              label="Dark Mode (demo only)"
            />
          </CardContent>
        </Card>

        {/* Profile Settings */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PaletteIcon sx={{ mr: 1, color: 'secondary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Profile Settings
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TextField
              label="Display Name"
              value="Jane Doe"
              fullWidth
              margin="normal"
            />
            <TextField
              label="Email"
              value="jane.doe@company.com"
              fullWidth
              margin="normal"
            />
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ mt: 2 }}
            >
              Save Changes
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}; 